import { useCallback, useEffect, useRef, useState } from 'react';
import { useConfigService } from '@thd-nucleus/experience-context';
import {
  useAddToCart,
  useDeleteLineItem,
  useLazyGetCart,
  useUpdateCart
} from '@thd-olt-component-react/cart-common-data';
import { cartFunctions, isValidRawCart, updateCartCountInHeader } from '../utils/promo-cart-utils';
import { FEATURE_SWITCH_KEYS } from '../utils/constants';
import { usePromoModel } from './usePromoModel';
import { useNewRelicLogging } from './useNewRelicLogging';

const {
  fetchCartHelper, updateCartHelper, addToCartHelper, bypassCartHelper
} = cartFunctions;

export const useCart = ({ initializeCall = true, bypassCartLogic = false }) => {
  const isMounted = useRef(false);
  const [currentCartModel, setCurrentCartModel] = useState();
  const [frozenCartModel, setFrozenCartModel] = useState();
  const [addToCartHandler] = useAddToCart();
  const [getCartItems, cartItemsResponse] = useLazyGetCart();
  const { updateItemQuantity } = useUpdateCart();
  const [deleteItems] = useDeleteLineItem();
  const isCartGraphQLEnabled = useConfigService(FEATURE_SWITCH_KEYS.enableCartGraphQL) || false;
  // If a user adds/updates items in cart before initial cart call comes back
  const initialFetchComplete = useRef(false);
  const [isCartLoading, setIsCartLoading] = useState(false);
  const { sendErrorsToNewRelic, sendDataToNewRelic } = useNewRelicLogging();
  const { experienceTag, subExperience } = usePromoModel();

  const handleReturnedCartModel = (newCartModel) => {
    if (isMounted.current) {
      initialFetchComplete.current = true;
      setCurrentCartModel(newCartModel);
    }
  };

  useEffect(() => {
    isMounted.current = true;
    // listen to generic add to cart events and handle the results
    const handleAtcSuccess = ({ eventName, output }) => {
      const { data = {}, CartModel } = output || {};
      const { addToCart: cartModel = CartModel } = data;
      if (eventName === 'success' && isValidRawCart(cartModel)) {
        const props = {
          actionName: 'ThdBindingAddToCartCall',
          callType: 'addToCart_Thd_Binding',
          experienceTag,
          subExperience
        };
        sendDataToNewRelic(props);
        handleReturnedCartModel(cartModel);
      }
    };
    window.LIFE_CYCLE_EVENT_BUS.on('add-to-cart.success', handleAtcSuccess);
    return () => {
      isMounted.current = false;
      window.LIFE_CYCLE_EVENT_BUS.off('add-to-cart.success', handleAtcSuccess);
    };
  }, [experienceTag, subExperience]);

  useEffect(() => {
    if (bypassCartLogic) {
      setIsCartLoading(true);
      bypassCartHelper()
        .then((emptyCartResponse) => {
          if (!initialFetchComplete.current) {
            handleReturnedCartModel(emptyCartResponse);
          }
        })
        .finally(() => {
          if (isMounted.current) setIsCartLoading(false);
        });
      setIsCartLoading(false);
      return;
    }

    if (isCartGraphQLEnabled) {
      if (!cartItemsResponse.loading && !cartItemsResponse.called) {
        setIsCartLoading(true);
        getCartItems();
      }
    } else if (initializeCall && !initialFetchComplete.current) {
      setIsCartLoading(true);
      fetchCartHelper()
        .then((newCartModel) => {
          if (!initialFetchComplete.current) {
            handleReturnedCartModel(newCartModel);
          }
        })
        .finally(() => {
          if (isMounted.current) setIsCartLoading(false);
        });
    }
  }, [initializeCall, initialFetchComplete, isCartGraphQLEnabled, cartItemsResponse, getCartItems]);

  const fetchCart = useCallback(() => {
    if (isMounted.current) {
      setIsCartLoading(true);
      try {
        if (isCartGraphQLEnabled) {
          setIsCartLoading(true);
          getCartItems();
          return cartItemsResponse;
        }

        // Non-GraphQL cart processing
        return fetchCartHelper()
          .then((newCartModel) => {
            handleReturnedCartModel(newCartModel);
            const props = { actionName: 'PromotionProductsCart', callType: 'fetchCart' };
            sendDataToNewRelic(props);
            return newCartModel;
          })
          .finally(() => {
            if (isMounted.current) setIsCartLoading(false);
          });
      } catch (error) {
        const props = { actionName: 'PromotionProductsCartError', errorMessage: error.message, callType: 'fetchCart' };
        sendErrorsToNewRelic(props);
        setIsCartLoading(false);
        return Promise.reject(error);
      } finally {
        if (isMounted.current) setIsCartLoading(false);
      }
    } else {
      return Promise.reject(new Error('useCart is unmounted'));
    }
  }, [cartItemsResponse, sendErrorsToNewRelic, sendDataToNewRelic, isCartGraphQLEnabled, getCartItems]);

  useEffect(() => {
    if (isCartGraphQLEnabled) {
      const processCartResponse = () => {
        const CartModel = cartItemsResponse?.data?.cartInfo;
        if (!initialFetchComplete.current && CartModel) {
          handleReturnedCartModel(CartModel);
          setIsCartLoading(false);
        }
      };
      processCartResponse();
    }
  }, [cartItemsResponse, isCartGraphQLEnabled, fetchCart]);

  // should always have currentCartModel in dep array
  useEffect(() => {
    if (!frozenCartModel) setIsCartLoading(false);
  }, [currentCartModel, frozenCartModel]);

  const addToCart = useCallback((cartRequest) => {
    if (bypassCartLogic) return Promise.resolve(null);
    setIsCartLoading(true);
    try {
      const cartCallHandler = isCartGraphQLEnabled ? addToCartHandler(cartRequest) : addToCartHelper(cartRequest);
      return cartCallHandler
        .then((newCartModel) => {
          const cartResponse = isCartGraphQLEnabled ? newCartModel?.data?.addToCart : newCartModel;
          handleReturnedCartModel(cartResponse);
          updateCartCountInHeader(cartResponse);
          window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-cart.success', { CartModel: newCartModel });
          const props = { actionName: 'PromotionProductsCart', callType: 'addToCart' };
          sendDataToNewRelic(props);
          if (isMounted.current) setIsCartLoading(false);

          return newCartModel;
        })
        .catch((error) => {
          const props = {
            actionName: 'PromotionProductsCartError',
            errorMessage: error.message,
            callType: 'addToCart'
          };
          sendErrorsToNewRelic(props);
          initialFetchComplete.current = false;
          return fetchCart();
        });
    } catch (error) {
      const props = { actionName: 'PromotionProductsCartError', errorMessage: error.message, callType: 'addToCart' };
      sendErrorsToNewRelic(props);
      setIsCartLoading(false);
      return Promise.reject(error);
    }
  }, [addToCartHandler, fetchCart, sendErrorsToNewRelic, sendDataToNewRelic, isCartGraphQLEnabled]);

  const updateCart = useCallback((cartRequest) => {
    if (bypassCartLogic) return Promise.resolve(null);
    setIsCartLoading(true);
    try {
      const cartCallHandler = isCartGraphQLEnabled
        ? updateItemQuantity(cartRequest?.newQuantity, cartRequest?.item)
        : updateCartHelper(cartRequest);
      return cartCallHandler
        .then((newCartModel) => {
          const cartResponse = isCartGraphQLEnabled ? newCartModel?.data?.updateCart : newCartModel;
          handleReturnedCartModel(cartResponse);
          updateCartCountInHeader(cartResponse);
          window.LIFE_CYCLE_EVENT_BUS.trigger('add-to-cart.success', { CartModel: newCartModel });
          const props = { actionName: 'PromotionProductsCart', callType: 'update' };
          sendDataToNewRelic(props);
          if (isMounted.current) setIsCartLoading(false);
          return newCartModel;
        })
        .catch((error) => {
          const props = { actionName: 'PromotionProductsCartError', errorMessage: error.message, callType: 'update' };
          sendErrorsToNewRelic(props);
          initialFetchComplete.current = false;
          return fetchCart();
        });
    } catch (error) {
      const props = { actionName: 'PromotionProductsCartError', errorMessage: error.message, callType: 'update' };
      sendErrorsToNewRelic(props);
      setIsCartLoading(false);
      return Promise.reject(error);
    }
  }, [fetchCart, sendErrorsToNewRelic, sendDataToNewRelic, isCartGraphQLEnabled, updateItemQuantity]);

  const deleteCart = useCallback((id) => {
    if (bypassCartLogic) return Promise.resolve(null);
    setIsCartLoading(true);
    try {
      const cartCallHandler = deleteItems([id]);
      return cartCallHandler
        .then((newCartModel) => {
          handleReturnedCartModel(newCartModel.data.deleteItem);
          updateCartCountInHeader(newCartModel.data.deleteItem);
          const props = { actionName: 'PromotionProductsCart', callType: 'delete' };
          sendDataToNewRelic(props);
          if (isMounted.current) setIsCartLoading(false);
          return newCartModel.data.deleteItem;
        })
        .catch((error) => {
          const props = { actionName: 'PromotionProductsCartError', errorMessage: error.message, callType: 'delete' };
          sendErrorsToNewRelic(props);
          initialFetchComplete.current = false;
          return fetchCart();
        });
    } catch (error) {
      const props = { actionName: 'PromotionProductsCartError', errorMessage: error.message, callType: 'delete' };
      sendErrorsToNewRelic(props);
      setIsCartLoading(false);
      return Promise.reject(error);
    }
  }, [fetchCart, deleteItems]);

  // when consumer calls freezeCart it takes the current value of cart and stores it in state,
  // it also returns a function that will "unfreeze" the cart by resetting it to null
  const freezeCart = useCallback(() => {
    if (isMounted.current) setFrozenCartModel(currentCartModel);

    return () => {
      if (isMounted.current) setFrozenCartModel(null);
    };
  }, [currentCartModel]);

  return {
    // if cart is "frozen" return the frozen value of cart, otherwise return the latest
    CartModel: frozenCartModel || currentCartModel,
    addToCart,
    updateCart,
    deleteCart,
    isCartLoading,
    setIsCartLoading,
    freezeCart,
    setCurrentCartModel,
  };
};
